import { useCallback, useEffect, useMemo, useState } from 'react'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'

type StoredVersionReportPageState = {
  versionUuid: string
}

export const usePageState = (
  functionUuid: string,
  projectUuid: string
): {
  storedVersionUuid: string
  store: (override?: Partial<StoredVersionReportPageState>) => void
  initialized: boolean
} => {
  const [storedVersionUuid, setStoredVersionUuid] = useState<string>('')
  const [initialized, setInitialized] = useState<boolean>(false)
  const key = useMemo(
    () => `${UiStateKey.PageState}-${projectUuid}`,
    [projectUuid]
  )

  const restorePageState = useCallback(async () => {
    setInitialized(false)
    try {
      const response = await uiStates.get({
        applicationFunctionUuid: functionUuid,
        key: key,
        scope: UiStateScope.User,
      })
      const stored = response.json
      if (stored.value) {
        const payload: StoredVersionReportPageState = JSON.parse(stored.value)
        payload.versionUuid && setStoredVersionUuid(payload.versionUuid)
      }
    } catch (error) {
      console.error('Failed to restore page state:', error)
    } finally {
      setInitialized(true)
    }
  }, [functionUuid, key])

  const store = useCallback(
    (override?: Partial<StoredVersionReportPageState>) => {
      const value: StoredVersionReportPageState = {
        versionUuid: override?.versionUuid ?? storedVersionUuid,
      }
      uiStates.update(
        {
          key: key,
          scope: UiStateScope.User,
          value: JSON.stringify(value),
        },
        functionUuid
      )
    },
    [functionUuid, storedVersionUuid, key]
  )

  useEffect(() => {
    restorePageState()
  }, [restorePageState])

  return {
    storedVersionUuid,
    store,
    initialized,
  }
}
